import { isValidPhoneNumber } from 'libphonenumber-js';
import type { Field, Messages } from '../@types';
/**
 * Validate an Address Field
 *
 * @param messages - i18n object including error messages
 * @param field - The `Field` from the address validation service to validate against
 */
export const validate = (
    messages: Messages | undefined,
    field: Field,
    displayName: string,
    country?: string,
    shouldValidateNonLatin = true,
    isKlarna = false,
    externalErrors?: string[],
) => (originalValue: string | number | undefined): Array<string> => {
        const { mandatory, maxLength, minLength, regex, values } = field;
        const errors: Array<string> = [];
        const value = originalValue?.toString?.().trim();
        // eslint-disable-next-line no-control-regex
        const nonLatin = shouldValidateNonLatin && value ? value.match(/[^\u0000-\u007F\u0080-\u00FF]/gu) : null;
        const fieldName = field.wcsType?.name.toLowerCase();

        if (messages?.errors) {
            if (!value && mandatory && messages.errors) {
                messages.errors.required && errors.push(messages.errors.required({ selectMessageField: fieldName || 'other', field: displayName }));
            } else if (value) {
                if (nonLatin != null) {
                    messages.errors.invalidCharacters &&
                        errors.push(messages.errors.invalidCharacters({ nonLatin: nonLatin.join(' '), selectMessageField: fieldName }));
                } else if (regex && !new RegExp(regex).test(value)) {
                    if (fieldName == 'pccc') {
                        messages.errors.invalidPccc && errors.push(messages.errors.invalidPccc({ field: field.displayType || 'other', name: displayName }));
                    } else if (field?.displayType === 'EIRCODE') {
                        messages.errors.eircode && errors.push(messages.errors.eircode());
                    } else {
                        messages.errors.invalid && errors.push(messages.errors.invalid({ field: displayName, selectMessageField: fieldName }));
                    }
                } else if (maxLength && value.length > maxLength) {
                    messages.errors.maxLength &&
                        errors.push(
                            messages.errors.maxLength({
                                characters: String(maxLength),
                                selectMessageField: `${fieldName}${country || ''}`,
                            }),
                        );
                } else if (minLength && value.length < minLength) {
                    messages.errors.minLength &&
                        errors.push(
                            messages.errors.minLength({
                                characters: String(minLength),
                                selectMessageField: `${fieldName}${country || ''}`,
                            }),
                        );
                } else if (
                    values &&
                    values.length > 0 &&
                    typeof values.find((thisVal) => value === (typeof thisVal !== 'string' ? thisVal.value : thisVal)) === 'undefined'
                ) {
                    messages.errors.invalid && errors.push(messages.errors.invalid({ field: displayName, selectMessageField: fieldName }));
                } else if (isKlarna && field.type === 'PHONE' && !isValidPhoneNumber(value)) {
                    messages.errors.invalidPhoneFormat && errors.push(messages.errors.invalidPhoneFormat());
                } else if (externalErrors?.length) {
                    externalErrors.forEach((externalError) => {
                        const error = externalError.toLowerCase();

                        if (error === fieldName) {
                            switch (fieldName) {
                                case 'pccc':
                                    messages.errors?.invalidPccc &&
                                        errors.push(messages.errors.invalidPccc({ field: displayName, selectMessageField: fieldName }));
                                    break;
                                case 'zipcode':
                                    messages.errors?.invalidZipCode && errors.push(messages.errors.invalidZipCode());
                                    break;
                                default:
                                    messages.errors?.invalid && errors.push(messages.errors.invalid({ field: displayName, selectMessageField: fieldName }));
                            }
                        }
                    });
                }
            }
        }

        return errors;
    };

